/**
 * Caché local (IndexedDB) del último ZIP de exportación MongoDB por periodo.
 * Aislado de descarga automática SAT / TaxRepository en disco.
 */

const DB_NAME = "visor_fiscal_export_zip_v1";
const STORE = "entries";
const LS_LAST = "visor:export_zip_last_meta";
const MAX_ENTRIES = 8;

export function buildExportCacheKey({ clientId, fechaInicio, fechaFin, direccion, includePdf }) {
  const dir = (direccion || "todos").trim().toLowerCase();
  return [clientId, fechaInicio, fechaFin, dir, includePdf ? "pdf" : "xml"].join("|");
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB no disponible"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onerror = () => reject(req.error || new Error("No se pudo abrir caché"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
  });
}

export function getLastExportMeta() {
  try {
    const raw = localStorage.getItem(LS_LAST);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Limpia metadatos locales al cambiar periodo o cliente (nueva búsqueda). */
export function clearLastExportMeta() {
  setLastExportMeta(null);
}

function setLastExportMeta(meta) {
  try {
    if (meta) localStorage.setItem(LS_LAST, JSON.stringify(meta));
    else localStorage.removeItem(LS_LAST);
  } catch {
    /* ignore */
  }
}

async function pruneOldEntries(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const allReq = store.getAll();
    allReq.onsuccess = () => {
      const rows = (allReq.result || []).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      const toDrop = rows.slice(MAX_ENTRIES);
      toDrop.forEach((row) => store.delete(row.key));
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** @returns {Promise<{ blob: Blob, filename: string, createdAt: number } | null>} */
export async function getCachedExportZip(cacheKey) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(cacheKey);
      req.onsuccess = () => {
        const row = req.result;
        if (!row?.blob || !row.filename) {
          resolve(null);
          return;
        }
        resolve({
          blob: row.blob,
          filename: row.filename,
          createdAt: row.createdAt || 0,
        });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function saveCachedExportZip(cacheKey, blob, filename, meta = {}) {
  try {
    const db = await openDb();
    const createdAt = Date.now();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({
        key: cacheKey,
        blob,
        filename,
        createdAt,
        ...meta,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    await pruneOldEntries(db);
    setLastExportMeta({
      cacheKey,
      filename,
      createdAt,
      label: meta.label || "",
      clientId: meta.clientId || "",
    });
  } catch {
    /* caché opcional */
  }
}

export function formatCacheAge(createdAt) {
  if (!createdAt) return "";
  const min = Math.round((Date.now() - createdAt) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h === 1 ? "hace 1 h" : `hace ${h} h`;
}
